import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AuthActions } from "src/redux/Auth/actions";
import Button from "src/components/common/Button";
import styles from "./styles.module.scss";

// How often to compare the clock against the session's end. An interval rather
// than a single long timer, because a suspended laptop does not fire timers it
// slept through, and the banner has to be right immediately on wake.
const TICK_MS = 30 * 1000;

/**
 * Shows a non-dismissable banner once the session has reached its daily sign-out,
 * so the user is told to re-authenticate rather than discovering it when their
 * next action fails.
 *
 * Advisory only. The gateway stops accepting the session at the same instant
 * whatever this component does, so a tampered-with or stopped timer changes
 * nothing about access. The only action is to sign in again, and the banner never
 * navigates on its own, so nothing in progress is lost without the user acting.
 */
const ReauthBanner = () => {
    const dispatch = useDispatch();
    const [expiresAt, setExpiresAt] = useState(null);
    const [expired, setExpired] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const resp = await fetch("/auth/me", { credentials: "include" });
                // 204 is "not signed in", which is not this banner's concern.
                if (!resp.ok || resp.status === 204) return;
                const { session_expires_at_ms: ms } = await resp.json();
                if (!cancelled && typeof ms === "number") setExpiresAt(ms);
            } catch (e) {
                // Transient: without an expiry the banner stays hidden and the
                // gateway still enforces the sign-out.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (expiresAt === null) return undefined;
        const check = () => {
            if (Date.now() >= expiresAt) setExpired(true);
        };
        check();
        const id = setInterval(check, TICK_MS);
        return () => clearInterval(id);
    }, [expiresAt]);

    if (!expired) return null;

    return (
        <div className={styles.banner} data-testid="reauth-banner">
            <span className={styles.message}>
                This session requires re-authentication.
            </span>
            <Button
                value="Sign in"
                onClick={() => dispatch(AuthActions.Logout())}
                dataTestId="reauth-sign-in"
            />
        </div>
    );
};

export default ReauthBanner;
